import dayjs from '@/lib/dayjs'
import { formatCurrency } from '@/lib/utils'

export const DEPOSIT_STATUS_LABELS = {
  paid: 'Paid',
  pending: 'Pending',
  in_transit: 'On the way',
  failed: 'Failed',
  canceled: 'Canceled',
}

export const DEPOSIT_STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  ...Object.entries(DEPOSIT_STATUS_LABELS).map(([value, label]) => ({ value, label })),
]

export function formatMinorCurrency(amountMinor, currency = 'usd') {
  return formatCurrency(Number(amountMinor ?? 0) / 100, String(currency || 'usd').toUpperCase())
}

// Fees reduce the deposit, so show them as subtractions.
export function negateMinor(amountMinor) {
  return amountMinor == null ? amountMinor : 0 - Number(amountMinor)
}

export function getDepositCurrencySuffix(currency) {
  const currencyCode = String(currency || 'usd').toUpperCase()

  return currencyCode === 'USD' ? '' : ` (${currencyCode})`
}

export function formatDepositDate(value, fallback = 'Date unavailable') {
  const date = dayjs(value)

  return date.isValid() ? date.format('MMM D, YYYY') : fallback
}

export function formatDepositDateTime(value, fallback = 'Not recorded') {
  const date = dayjs(value)

  return date.isValid() ? date.format('MMM D, YYYY [at] h:mm A') : fallback
}

export function formatDepositSpeed(value) {
  if (value === 'instant') return 'Instant'
  if (value === 'standard') return 'Standard'

  return 'Not recorded'
}

export function isBankWithdrawal(settlement) {
  return settlement?.movement_kind === 'bank_withdrawal'
}

export function getDepositDescription(deposit) {
  const destination = deposit.destination?.summary ?? 'your bank'
  const date = formatDepositDate(
    deposit.status === 'failed'
      ? (deposit.failed_at ?? deposit.effective_deposit_at)
      : deposit.status === 'canceled'
        ? (deposit.canceled_at ?? deposit.effective_deposit_at)
        : deposit.effective_deposit_at
  )
  const isEstimated = deposit.effective_date_source !== 'expected_arrival'
  const withdrawal = isBankWithdrawal(deposit)

  if (withdrawal) {
    if (deposit.status === 'failed') return `Withdrawal from ${destination} failed on ${date}.`
    if (deposit.status === 'canceled')
      return `Withdrawal from ${destination} was canceled on ${date}.`
    if (deposit.status === 'paid') return `Collected from ${destination} on ${date}.`

    return `Expected to be collected from ${destination} on ${date}.`
  }

  if (deposit.status === 'failed') return `Deposit to ${destination} failed on ${date}.`
  if (deposit.status === 'canceled') return `Deposit to ${destination} was canceled on ${date}.`
  if (deposit.status === 'paid') {
    if (isEstimated) return `Deposited to ${destination}. Deposit date estimated as ${date}.`

    return `Deposited to ${destination} on ${date}.`
  }
  if (isEstimated) return `Expected at ${destination}. Deposit date estimated as ${date}.`

  return `Expected at ${destination} on ${date}.`
}

// The period summary covers completed deposits on every page, so it becomes the table's total row.
export function getDepositTotalRows(summaryCurrencies = []) {
  return summaryCurrencies.map((summary) => ({
    ...summary,
    id: `total-${summary.currency}`,
    isTotal: true,
    amount_minor: summary.deposited_amount_minor,
  }))
}

// Instant and manual payouts and disputes get their own lines; other activity is what remains.
export function getOtherActivityMinor(amounts) {
  if (amounts?.account_activity_minor == null) return null

  return (
    Number(amounts.account_activity_minor) -
    Number(amounts.instant_payouts_minor ?? 0) -
    Number(amounts.manual_payouts_minor ?? 0) -
    Number(amounts.dispute_activity_minor ?? 0)
  )
}

// Use Stripe's names so florists recognize payouts they started in Stripe.
export function getManualPayoutName(settlement) {
  return settlement?.method === 'instant' ? 'Instant Payout' : 'manual payout'
}

export function isManualDeposit(settlement) {
  return !isBankWithdrawal(settlement) && settlement?.automatic === false
}

export function getMovementName(settlement) {
  if (isBankWithdrawal(settlement)) return 'bank withdrawal'
  if (isManualDeposit(settlement)) return getManualPayoutName(settlement)

  return 'deposit'
}

export function getSourceSettlementLinkLabel(settlement) {
  if (isBankWithdrawal(settlement)) return 'View earlier withdrawal'
  if (isManualDeposit(settlement)) return `View ${getManualPayoutName(settlement)}`

  return 'View earlier deposit'
}

export function getEntryActivityLabel(entry) {
  const labels = {
    payment: 'Payment',
    refund: 'Refund',
    fee: 'Fee',
    fee_refund: 'Fee refunded',
    dispute: 'Dispute',
    balance_reserve: 'Balance reserve',
    reserve_released: 'Reserve released',
    contribution: 'Contribution',
    earlier_deposit: 'Deposited earlier',
    earlier_manual_deposit:
      entry.source_settlement?.method === 'instant' ? 'Instant Payout' : 'Manual payout',
    instant_payout_advance: 'Instant Payout advance',
    instant_payout_advance_repaid: 'Instant Payout advance repaid',
    earlier_bank_withdrawal: 'Collected from your bank earlier',
    earlier_failed_deposit: 'From an earlier failed deposit',
    earlier_canceled_deposit: 'From an earlier canceled deposit',
    uncollected_bank_withdrawal: 'Uncollected bank withdrawal',
    other_account_activity: 'Other account activity',
  }

  return labels[entry.activity_kind] ?? formatWords(entry.type)
}

function formatWords(value) {
  if (!value) return 'Other account activity'

  return String(value)
    .replaceAll('_', ' ')
    .replace(/^\w/, (letter) => letter.toUpperCase())
}
